import { Location as ALocation, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { LOCAL_STORAGE, SESSION_STORAGE } from '@ng-web-apis/common';
import { Apollo, QueryRef } from 'apollo-angular';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  Unsubscribable,
  catchError,
  delay,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  tap,
} from 'rxjs';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import {
  GetAllConversationQuery,
  GetAllConversationQueryVariables,
} from 'src/app/pages/messages/messages.query.generated';
import { PrivacyPolicyVersion, TermsAndConditionsVersion, User } from 'src/types/graphql';
import { SplashScreenService } from '../../_shared/components/splash-screen/splash-screen.service';
import { StorageService } from '../storage/storage.service';
import {
  UserDeviceCreateGQL,
  UserDeviceDeleteGQL,
  UserRefuseGeolocationGQL,
  UserSignOutGQL,
} from './user.mutation.generated';
import { CurrentPoliciesGQL, GetUserAllConversationGQL, MeGQL } from './user.query.generated';
import { MessageCreatedGQL, NotificationCreatedGQL } from './user.subscription.generated';

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  user: Partial<User>;
  user$: Observable<Partial<User>>;
  unreadNotifications$: Observable<number>;
  unreadNotificationsSubject: BehaviorSubject<number>;
  unreadNotificationsBookings$: Observable<number>;
  unreadNotificationsBookingsSubject: BehaviorSubject<number>;
  unreadNotificationsDashboard$: Observable<number>;
  unreadNotificationsDashboardSubject: BehaviorSubject<number>;
  unreadMessages$: Observable<number>;
  unreadMessagesSubject: BehaviorSubject<number>;
  privacyPolicy: PrivacyPolicyVersion;
  termsAndConditions: TermsAndConditionsVersion;
  notLoggedBackTo = '';
  firebaseToken: Token;
  messageSub: Subscription;
  notificationSub: Subscription;
  bgNotificationAction: ActionPerformed;
  public userSubject: BehaviorSubject<Partial<User>>;
  private getAllConversationQuery: QueryRef<GetAllConversationQuery, GetAllConversationQueryVariables>;
  private subscriptions: Unsubscribable[] = [];

  constructor(
    private router: Router,
    private meGQL: MeGQL,
    private getAllConversationGQL: GetUserAllConversationGQL,
    private currentPoliciesGQL: CurrentPoliciesGQL,
    private messageCreatedGQL: MessageCreatedGQL,
    private notificationCreatedGQL: NotificationCreatedGQL,
    private userDeviceCreateGQL: UserDeviceCreateGQL,
    private userDeviceDeleteGQL: UserDeviceDeleteGQL,
    private userRefuseGeolocationGQL: UserRefuseGeolocationGQL,
    private apollo: Apollo,
    private ngZone: NgZone,
    private splashScreenService: SplashScreenService,
    private storageService: StorageService,
    private routeTranslateService: RouteTranslateService,
    private userSignOut: UserSignOutGQL,
    private aLocation: ALocation,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(LOCAL_STORAGE) private localStorage: Storage,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
  ) {
    this.unreadNotificationsSubject = new BehaviorSubject(0);
    this.unreadNotificationsBookingsSubject = new BehaviorSubject(0);
    this.unreadNotificationsDashboardSubject = new BehaviorSubject(0);
    this.unreadMessagesSubject = new BehaviorSubject(0);
    this.userSubject = new BehaviorSubject(this.user);
    this.unreadNotifications$ = this.unreadNotificationsSubject.asObservable();
    this.unreadNotificationsBookings$ = this.unreadNotificationsBookingsSubject.asObservable();
    this.unreadNotificationsDashboard$ = this.unreadNotificationsDashboardSubject.asObservable();
    this.unreadMessages$ = this.unreadMessagesSubject.asObservable();
    this.user$ = this.userSubject.asObservable();
    this.getAllConversationQuery = this.getAllConversationGQL.watch({}, { fetchPolicy: 'cache-first' });
    if (isPlatformBrowser(this.platformId)) {
      if (Capacitor.getPlatform() !== 'web') {
        PushNotifications.addListener('registration', token => {
          PushNotifications.removeAllDeliveredNotifications();
          // success callback
          this.synchronizeFirebaseToken(token);
        }).catch(error => {
          console.error(error);
        });
        PushNotifications.addListener('registrationError', error => {
          // success callback
          console.error(error.error);
        }).catch(error => {
          console.error(error);
        });
        PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
          setTimeout(() => {
            this.bgNotificationAction = { ...action };
            const path = action?.notification?.data?.path;
            if (path) {
              if (this.user) {
                PushNotifications.removeAllDeliveredNotifications();
                this.bgNotificationAction = null;
                this.router.navigateByUrl(path);
              } else {
                //this.initialNavigationService.setNavigation(path);
              }
            }
            PushNotifications.removeAllDeliveredNotifications();
          }, 1000);
        });
      }
    }
  }

  setupFirebaseNotifications() {
    if (Capacitor.getPlatform() !== 'web') {
      PushNotifications.checkPermissions()
        .then(status => {
          if (status.receive === 'granted') {
            PushNotifications.register()
              .then()
              .catch(error => console.log(error));
          } else if (status.receive === 'denied') {
            console.error('permission denied');
          } else {
            PushNotifications.requestPermissions().then(() => {
              this.setupFirebaseNotifications();
            });
          }
        })
        .catch(error => console.log(error));
    }
  }

  setUser(user: Partial<User>, skipCacheReset?: boolean, preventSubjectEmit?: boolean) {
    const notUserBefore = !this.user?.id;
    this.user = user ? { ...user } : undefined;
    if (!preventSubjectEmit) this.userSubject.next(user);
    if (isPlatformBrowser(this.platformId) && !!this.user && notUserBefore) {
      this.messageSub?.unsubscribe();
      this.notificationSub?.unsubscribe();
      this.initSubscriptions();
    }
    if (skipCacheReset) return;
    this.apollo.client.cache.reset().then(() => {
      if (Capacitor.getPlatform() !== 'web' && !!this.user) {
        this.setupFirebaseNotifications();
      }
    });
  }

  initUser(user: Partial<User>) {
    this.setUser(user, true, false);
    if (this.bgNotificationAction) {
      const path = this.bgNotificationAction?.notification?.data?.path;
      if (path && this.user) {
        PushNotifications.removeAllDeliveredNotifications();
        this.bgNotificationAction = null;
        this.aLocation.replaceState(path);
        // this.ngZone.run(() => {
        //   this.router.navigateByUrl(path);
        // });
      }
    }
    return from(this.apollo.client.cache.reset()).pipe(
      mergeMap(() => {
        if (Capacitor.isNativePlatform() && !!this.user) {
          const setupFirebaseNotifications = (): Observable<any> =>
            from(PushNotifications.checkPermissions()).pipe(
              mergeMap(status => {
                if (status.receive === 'granted') {
                  return from(PushNotifications.register());
                } else if (status.receive === 'denied') {
                  return of(null);
                } else {
                  return from(PushNotifications.requestPermissions()).pipe(
                    mergeMap(() => setupFirebaseNotifications()),
                  );
                }
              }),
            );
          return setupFirebaseNotifications().pipe(map(() => true));
        }
        return of(true);
      }),
    );
  }

  synchronizeFirebaseToken(token: any) {
    if (this.firebaseToken !== token) {
      this.firebaseToken = token;
      this.subscriptions.push(
        this.userDeviceCreateGQL
          .mutate({
            input: {
              token: token.value,
            },
          })
          .subscribe(),
      );
    }
  }

  unsynchronizeFirebaseToken(token: any) {
    this.firebaseToken = undefined;
    return this.userDeviceDeleteGQL.mutate({ token: token.value });
  }

  signOut(preventReload?: boolean) {
    if (Capacitor.getPlatform() !== 'web') {
      if (this.user.provider === 'google') {
        GoogleAuth.signOut()
          .then(() => this.sharrySignOut(preventReload))
          .catch(error => console.error(error));
      } else if (this.user.provider === 'facebook') {
        FacebookLogin.logout()
          .then(() => this.sharrySignOut(preventReload))
          .catch(error => console.error(error));
      } else {
        this.sharrySignOut(preventReload);
      }
    } else {
      this.sharrySignOut(preventReload);
    }
  }

  sharrySignOut(preventReload?: boolean) {
    if (!preventReload) {
      this.splashScreenService.showSplash('visible');
    }
    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.push(
        from(this.apollo.client.resetStore())
          .pipe(
            mergeMap(() => {
              if (Capacitor.getPlatform() !== 'web' && !!this.firebaseToken) {
                PushNotifications.removeAllDeliveredNotifications().then(() => console.log('remove all notifications'));
                return this.unsynchronizeFirebaseToken(this.firebaseToken);
              } else {
                return of(null);
              }
            }),
            mergeMap(() => this.userSignOut.mutate()),
            tap(() => {
              this.user = undefined;
              this.userSubject.next(null);
              this.notLoggedBackTo = undefined;
              const uuid = this.localStorage.getItem('cookie_preference_uuid');
              this.storageService.clear();
              this.localStorage.clear();
              if (uuid) {
                this.localStorage.setItem('cookie_preference_uuid', uuid);
              }
              this.sessionStorage.clear();
            }),
            mergeMap(() => {
              if (!preventReload) {
                return from(this.router.navigate([this.routeTranslateService.translateRoute('explore')])).pipe(
                  delay(1000),
                  tap(() => {
                    this.splashScreenService.showSplash('hidden');
                  }),
                );
              }
              return of(true);
            }),
          )
          .subscribe({
            next: () => {
              this.splashScreenService.showSplash('destroyed');
              this.subscriptions.forEach(subscription => subscription.unsubscribe());
              this.messageSub?.unsubscribe();
              this.notificationSub?.unsubscribe();
            },
          }),
      );
    }
  }

  clearStore() {
    return this.apollo.client.clearStore();
  }

  canDeactivate() {
    return !this.user || !!(!this.checkPrivacyPolicy() && this.user.confirmedAt);
  }

  readAllNotifications() {
    this.unreadNotificationsSubject.next(0);
    this.unreadNotificationsBookingsSubject.next(0);
    this.unreadNotificationsDashboardSubject.next(0);
  }

  fetchUserAndPolicy(preventSubjectEmit?: boolean) {
    return this.ngZone.runOutsideAngular(() =>
      forkJoin([
        this.meGQL.fetch({}, { fetchPolicy: 'network-only' }).pipe(catchError(_ => of({ data: { me: null } }))),
        this.currentPoliciesGQL.fetch(),
      ]).pipe(
        map(res => {
          this.privacyPolicy = res[1].data.privacyPolicy;
          this.termsAndConditions = res[1].data.termsAndConditions;
          this.setUser(res[0].data.me, false, preventSubjectEmit);
        }),
      ),
    );
  }

  setUserRefusedGeolocation(refusedGeolocation: boolean) {
    if (this.user) {
      this.subscriptions.push(
        this.userRefuseGeolocationGQL.mutate({ input: { id: this.user.id, refusedGeolocation } }).subscribe({
          next: () => (this.user.refusedGeolocation = refusedGeolocation),
          error: err => console.error(err),
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.messageSub?.unsubscribe();
    this.notificationSub?.unsubscribe();
    this.unreadNotificationsSubject.complete();
    this.unreadMessagesSubject.complete();
    this.userSubject.complete();
  }

  private initSubscriptions() {
    this.subscriptions.push(
      this.ngZone.runOutsideAngular(() =>
        this.getAllConversationQuery.valueChanges.subscribe({
          next: res => {
            this.unreadNotificationsSubject.next((res.data.allNotifications || []).filter(n => !n.readAt)?.length);
            this.unreadNotificationsDashboardSubject.next(
              (res.data.allNotifications || []).filter(n => !n.readAt && n.kind === 'cart_item_create_owner')?.length,
            );
            this.unreadNotificationsBookingsSubject.next(
              (res.data.allNotifications || []).filter(
                n => !n.readAt && (n.kind === 'cart_item_accepted' || n.kind === 'cart_item_rejected'),
              )?.length,
            );
            this.unreadMessagesSubject.next(
              (res.data.allConversations || []).filter(
                c => c.lastMessage && !c.lastMessage.readAt && !c.lastMessage.sender.isMe,
              )?.length,
            );
          },
        }),
      ),
    );
    this.messageSub = this.messageCreatedGQL
      .subscribe({ recipientId: this.user.id })
      .subscribe({ next: () => this.getAllConversationQuery.refetch() });
    this.notificationSub = this.notificationCreatedGQL
      .subscribe({ userId: this.user.id })
      .subscribe({ next: () => this.getAllConversationQuery.refetch() });
  }

  private checkPrivacyPolicy() {
    return (
      this.user &&
      (!this.user.privacyPolicyVersion ||
        !this.user.termsAndConditionsVersion ||
        this.user.privacyPolicyVersion.version !== this.privacyPolicy.version ||
        this.user.termsAndConditionsVersion.version !== this.termsAndConditions.version)
    );
  }
}
